/*
 * The one catalogue. Every area of the frontend keeps its copy beside its code — `core/shell/`,
 * `core/settings/`, a module's own `i18n/en.ts` — and this file is where those pieces become the
 * single typed catalogue the interface is checked against: `t("shell.nav.activity")` compiles
 * because the shell contributed that key, and a key nobody contributed does not compile at all,
 * which is what makes `react/jsx-no-literals` affordable (Q38).
 *
 * Two kinds of key exist. A key of *this* build is typed: the merge below is a value, so its keys
 * are a union and `t()` also knows which `{placeholders}` the text needs. A key that arrives at
 * runtime — an error key from the server (protocol.md §1.1), or a module's namespace loaded with
 * its route bundle through `addCatalogue` — is a plain string, and `translate()` renders it,
 * falling back to a sentence this build does know rather than showing a raw key at a person.
 */

import { en as activity } from "../activity/i18n/en.ts"
import { en as admin } from "../admin/i18n/en.ts"
import { en as auth } from "../auth/i18n/en.ts"
import { en as composer } from "../composer/i18n/en.ts"
import { en as docs } from "../docs/i18n/shell.ts"
import { en as emoji } from "../emoji/i18n/en.ts"
import { en as groups } from "../groups/i18n/en.ts"
import { en as list } from "../list/i18n/en.ts"
import { en as ops } from "../ops/i18n/en.ts"
import { en as people } from "../people/i18n/en.ts"
import { en as render } from "../render/i18n/en.ts"
import { en as search } from "../search/i18n/en.ts"
import { en as settings } from "../settings/i18n/en.ts"
import { en as shell } from "../shell/i18n/en.ts"
import { en as core } from "./en.ts"
import { interpolate, makeLookupT, makeT, pluralKey, type ArgsOf } from "./t.ts"

/** The whole of the copy this build ships, area by area. */
export const catalogue = {
	...core,
	...shell,
	...auth,
	...composer,
	...emoji,
	...people,
	...groups,
	...settings,
	...admin,
	...activity,
	...search,
	...ops,
	...render,
	...list,
	...docs,
} as const

export type Catalogue = typeof catalogue
export type Key = keyof Catalogue

/**
 * A key this build ships, or one that only exists at runtime — a module's namespace, a key the
 * server sent. `translate()` renders both, so a core component that only passes a key on takes this
 * rather than `Key`; the union keeps the completion of the known keys without shutting a module out.
 */
export type AnyKey = Key | (string & {})

export type Args<K extends Key> = ArgsOf<Catalogue, K>

/**
 * The keys whose text carries no placeholder. A list of labels — a navigation, a filter row, a
 * shortcut table — is typed with this rather than with `Key`, because `t(key)` can only be called
 * without arguments when every key in the union needs none.
 */
export type PlainKey = {
	[K in Key]: [keyof ArgsOf<Catalogue, K>] extends [never] ? K : never
}[Key]

/**
 * The namespaces loaded at runtime, keyed by the module that registered them. A module's keys are
 * not part of `Key`: they arrive with its route bundle, long after this file was type-checked.
 */
const namespaces = new Map<string, Record<string, string>>()

/**
 * Adds one module's namespace (`FrontendModule.i18n()`, modules.md §8). Registering the same module
 * twice replaces its entries rather than merging them, so a reloaded bundle cannot leave a stale
 * key behind. A namespace never shadows a key of this build: the typed catalogue wins.
 */
export function addCatalogue(owner: string, entries: Record<string, string>): void {
	namespaces.set(owner, entries)
}

/**
 * A module's typed `t`, reading the namespace registered under `owner`: `namespaceT<typeof en>(id)`,
 * with `en` imported as a type. The catalogue is then a value only in the chunk the module's
 * `i18n()` loads, so its copy stays out of the entry chunk while every key and placeholder is still
 * checked by the compiler. It answers once the router has registered the namespace, which the router
 * does before the first render (`routes/router.tsx`).
 */
export function namespaceT<C extends Record<string, string>>(owner: string) {
	return makeLookupT<C>((key) => namespaces.get(owner)?.[key])
}

/** The text of one key of this build, with its placeholders filled in. */
export const t = makeT(catalogue)

/** True when a string is a key this build itself ships — a module's namespace does not count. */
export function isKey(key: string): key is Key {
	return Object.prototype.hasOwnProperty.call(catalogue, key)
}

function lookup(key: string): string | undefined {
	if (isKey(key)) return catalogue[key]
	for (const entries of namespaces.values()) {
		const found = entries[key]
		if (found !== undefined) return found
	}
	return undefined
}

/**
 * The text of a key that arrived at runtime — from the server or from a module namespace — or of
 * `fallback` when nothing knows it. An older client must never render a raw key at a person.
 */
export function translate(
	key: string,
	args?: Record<string, string | number>,
	fallback: Key = "error.unknown",
): string {
	const template = lookupPlural(key, args) ?? catalogue[fallback]
	return args ? interpolate(template, args) : template
}

/**
 * The text of a key that is known, and `""` for one that is not — so a caller can try a second key
 * before it falls back. A refusal carries the server's own key and, less specific, its wire code
 * (protocol.md §1.1); `errorText` renders the first of the two this build can answer.
 */
export function textOfKey(key: string, args?: Record<string, string | number>): string {
	const template = lookupPlural(key, args)
	if (template === undefined) return ""
	return args ? interpolate(template, args) : template
}

/**
 * One key's text, in the form the count it carries asks for. The plural rule is the catalogue's and
 * not one accessor's (`pluralKey`): a module's key and a key the server registered reach a reader
 * through the two functions above, and "1 changes discarded." is what it costs when they do not
 * know it.
 */
function lookupPlural(
	key: string,
	args: Record<string, string | number> | undefined,
): string | undefined {
	const sibling = pluralKey(key, args)
	return (sibling === key ? undefined : lookup(sibling)) ?? lookup(key)
}

export * from "./format.ts"
